import { Link2 } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type MyFileItem } from '@apm/contracts';
import { copyText } from '../../../lib/clipboard';
import { ApiRequestError } from '../../../lib/http';
import { PreviewAction } from './preview-frame';

/**
 * Link riêng cho popup xem trước — APM-PVLINK.
 *
 * Mở popup ⇒ URL mang id của thứ đang xem, trên **chính trang đang đứng** (search param): đóng
 * popup là về đúng trang, Back đóng popup, và link dán cho người khác mở ra đúng popup ấy.
 *
 * - Tài liệu dự án: `/projects?projectId=…&tab=overview&doc=<documentId>` — tham số `doc` có từ
 *   thông báo "gửi duyệt tài liệu". Sổ chốt phương án cũng ghi `doc` (trang sổ và tab Tổng quan
 *   không cùng hiện), nhưng link **chép** luôn trỏ tab Tổng quan: người nhận cần tài liệu, không
 *   cần sổ đã xuất ra nó.
 * - Trang Tệp: `/files?preview=<fileId>`.
 *
 * Mọi id là uuid — chuỗi mà `JSON.parse` của router không đọc nổi, nên schema chuỗi là đúng (bẫy
 * APM-903b không áp). Link đi qua **đúng** API các màn hình đã dùng (luật #5): không có đường đọc
 * nào riêng cho link.
 */
export function documentLinkHref(projectId: string, documentId: string): string {
  return `/projects?${new URLSearchParams({ projectId, tab: 'overview', doc: documentId }).toString()}`;
}

export function fileLinkHref(fileId: string): string {
  return `/files?${new URLSearchParams({ preview: fileId }).toString()}`;
}

/**
 * Link "Chép link" của khung xem trước trang Tệp — `null` = không có nút.
 *
 * - Tệp của tôi: `/files?preview=<id>` — mở lại khung cho chủ tệp, và cho người được chia sẻ tệp
 *   ấy (trang Tệp chuyển họ sang `/shared/<lượt chia sẻ>`).
 * - Tệp của task / tài liệu / lỗi / dự án: chỗ gốc trong dự án (`location.href`; tài liệu thì mở
 *   sẵn popup của nó) — thành viên dự án xem được tệp ở đó, còn trang Tệp chỉ có tệp chính mình
 *   tải lên nên `?preview=` ở họ là trang trống.
 * - To-do: riêng của một người, không ai khác mở được ⇒ không có link.
 */
export function linkHrefOf(
  item: Pick<MyFileItem, 'id' | 'kind' | 'source' | 'location'>,
): string | null {
  if (item.kind !== 'file' || item.source === 'todo') return null;
  return item.source === 'mine' ? fileLinkHref(item.id) : item.location.href;
}

/** Link tuyệt đối để dán đi — theo origin đang đứng (IP LAN ở máy dev, tên miền ở production). */
export function absoluteLink(href: string, origin: string = window.location.origin): string {
  return new URL(href, origin).href;
}

/**
 * Lỗi "không tìm thấy" và "không có quyền" là **một** câu cho người dùng: tách hai câu là cho
 * người không có quyền biết tài liệu có tồn tại.
 */
export function isLinkUnavailable(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.status === 404 || error.status === 403);
}

/** Nút "Chép link" của khung xem trước — chép được cả khi mở app qua IP LAN (`lib/clipboard.ts`). */
export function CopyLinkAction({ href }: { readonly href: string }): ReactNode {
  const { t } = useTranslation();
  const copy = async (): Promise<void> => {
    const link = absoluteLink(href);
    if (await copyText(link)) toast.success(t('myFiles.preview.linkCopied'));
    else toast.error(t('myFiles.preview.copyFailed', { link }), { duration: 15_000 });
  };
  return (
    <PreviewAction
      label={t('myFiles.preview.copyLink')}
      icon={<Link2 aria-hidden />}
      onClick={() => void copy()}
    />
  );
}
